import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  fetchEmails,
  fetchEmailStats,
  emailKindLabel,
  emailKindCategory,
} from '../../api/admin.js';
import { useUrlParam } from '../../lib/useUrlTab.js';
import { useUrlPaginatedQuery } from '../../lib/useUrlPaginatedQuery.js';
import { formatDateTime, shortId } from '../../lib/format.js';
import { EmailDetailModal } from './EmailDetailModal.js';

function useDebouncedValue<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

const STATUS_CHIPS: Array<{ id: string; label: string }> = [
  { id: '', label: 'todos' },
  { id: 'sent', label: 'enviados' },
  { id: 'pending', label: 'pendientes' },
];

export function EmailsListPage(): ReactElement {
  const [qParam, setQParam] = useUrlParam('q');
  const [statusParam, setStatusParam] = useUrlParam('status');
  const [kindParam, setKindParam] = useUrlParam('kind');
  const [q, setQ] = useState(qParam ?? '');
  const status = statusParam ?? '';
  const kind = kindParam ?? '';
  const debounced = useDebouncedValue(q, 250);
  const pager = useUrlPaginatedQuery();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Persistir q solo cuando el debounce settle (no en cada tecla).
  useEffect(() => {
    setQParam(debounced || null);
  }, [debounced, setQParam]);

  const statsQuery = useQuery({
    queryKey: ['admin', 'emails', 'stats'],
    queryFn: () => fetchEmailStats(),
    staleTime: 60_000,
  });

  const query = useQuery({
    queryKey: ['admin', 'emails', { q: debounced, status, kind, cursor: pager.cursor }],
    queryFn: () => {
      const filters: Parameters<typeof fetchEmails>[0] = { limit: 50 };
      if (debounced) filters.q = debounced;
      if (status) filters.status = status;
      if (kind) filters.kind = kind;
      if (pager.cursor) filters.cursor = pager.cursor;
      return fetchEmails(filters);
    },
  });

  const kindOptions = statsQuery.data?.byKind ?? [];

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Emails</h1>
          <p className="page-sub">
            Engagement + crédito que el backend manda vía Resend. No incluye
            OTP/verificación/reset/welcome (van directo, sin registro) ni el
            contenido del email — sólo metadata del envío.
          </p>
        </div>
        {statsQuery.data ? (
          <div style={{ display: 'flex', gap: 'var(--s-5)' }}>
            <Stat label="Total" value={statsQuery.data.total} />
            <Stat label="Enviados" value={statsQuery.data.sent} />
            <Stat label="Pendientes" value={statsQuery.data.pending} />
          </div>
        ) : null}
      </div>

      <div className="filterbar">
        <input
          type="text"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            pager.reset();
          }}
          placeholder="email del destinatario…"
          style={{ minWidth: 240 }}
          data-testid="emails-search"
        />
        <div className="group">
          <span className="label" style={{ margin: 0 }}>
            Tipo
          </span>
          <select
            value={kind}
            onChange={(e) => {
              setKindParam(e.target.value || null);
              pager.reset();
            }}
            data-testid="emails-kind"
          >
            <option value="">todos</option>
            {kindOptions.map((k) => (
              <option key={k.kind} value={k.kind}>
                {emailKindLabel(k.kind)} ({k.count})
              </option>
            ))}
          </select>
        </div>
        <div className="group">
          <span className="label" style={{ margin: 0 }}>
            Estado
          </span>
          <div className="chip-group">
            {STATUS_CHIPS.map((s) => (
              <button
                key={s.id || 'all'}
                type="button"
                className={`chip${status === s.id ? ' is-on' : ''}`}
                onClick={() => {
                  setStatusParam(s.id || null);
                  pager.reset();
                }}
                data-testid={`emails-status-${s.id || 'all'}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {query.isPending ? (
        <div className="loading">Cargando…</div>
      ) : query.isError ? (
        <div className="loading">Error: {query.error.message}</div>
      ) : (
        <>
          <table className="table" data-testid="emails-table">
            <thead>
              <tr>
                <th>Estado</th>
                <th>Tipo</th>
                <th>Usuario</th>
                <th>Enviado</th>
                <th>Creado</th>
                <th>Período (dedupe)</th>
              </tr>
            </thead>
            <tbody>
              {query.data.items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="muted" style={{ textAlign: 'center' }}>
                    Sin emails
                  </td>
                </tr>
              ) : (
                query.data.items.map((m) => (
                  <tr
                    key={m.id}
                    className="row"
                    onClick={(e) => {
                      // El email del user tiene su propio Link (preserva
                      // right-click "abrir en pestaña"); no abrir el modal ahí.
                      if ((e.target as HTMLElement).closest('a')) return;
                      setSelectedId(m.id);
                    }}
                    data-testid={`email-row-${m.id}`}
                  >
                    <td>
                      <span className={`badge ${m.status === 'sent' ? 'b-ok' : 'b-warn'}`}>
                        {m.status === 'sent' ? 'enviado' : 'pendiente'}
                      </span>
                    </td>
                    <td>
                      <span>{emailKindLabel(m.kind)}</span>{' '}
                      <span className="badge b-neutral">{emailKindCategory(m.kind)}</span>
                    </td>
                    <td>
                      {m.userEmail ? (
                        <Link to={`/users/${m.userId}`}>{m.userEmail}</Link>
                      ) : (
                        <Link to={`/users/${m.userId}`} className="mono tt-tert">
                          {shortId(m.userId)}
                        </Link>
                      )}
                    </td>
                    <td className="tt-tert mono">{m.sentAt ? formatDateTime(m.sentAt) : '—'}</td>
                    <td className="tt-tert mono">{formatDateTime(m.createdAt)}</td>
                    <td className="mono tt-tert" title={m.periodKey}>
                      {m.periodKey}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div className="flex ai-c gap-4 pad-6">
            <button
              type="button"
              className="btn"
              disabled={!pager.canGoPrev}
              onClick={pager.goPrev}
            >
              ← Anterior
            </button>
            <button
              type="button"
              className="btn"
              disabled={!query.data.nextCursor}
              onClick={() => pager.goNext(query.data.nextCursor)}
            >
              Siguiente →
            </button>
          </div>
        </>
      )}
      {selectedId ? (
        <EmailDetailModal emailId={selectedId} onClose={() => setSelectedId(null)} />
      ) : null}
    </>
  );
}

function Stat({ label, value }: { label: string; value: number }): ReactElement {
  return (
    <div className="kpi" data-testid={`emails-stat-${label.toLowerCase()}`}>
      <span className="kpi-label">{label}</span>
      <div className="kpi-value-row">
        <span className="kpi-value">{value.toLocaleString('es-AR')}</span>
      </div>
    </div>
  );
}
